import React, { useState, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Expand a range into array of individual dates (half-open, excludes check_out day)
function expandRange(checkIn, checkOut) {
  const dates = [];
  const start = parseYMD(checkIn);
  const end = parseYMD(checkOut);
  if (!start || !end) return dates;
  const cur = new Date(start);
  while (cur < end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

export default function DateRangePicker({ checkIn, checkOut, onChange, lang = "EN" }) {
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then((res) => {
        const ranges = res.data.blocked || [];
        const all = [];
        ranges.forEach((r) => {
          all.push(...expandRange(r.check_in, r.check_out));
        });
        setBlockedDates(all);
      })
      .catch(() => setBlockedDates([]))
      .finally(() => setLoading(false));
  }, []);

  const selected = {
    from: parseYMD(checkIn),
    to: parseYMD(checkOut),
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const disabledMatcher = [{ before: today }, ...blockedDates];

  const handleSelect = (range) => {
    if (!range) {
      onChange({ check_in: "", check_out: "" });
      return;
    }
    onChange({
      check_in: range.from ? toYMD(range.from) : "",
      check_out: range.to ? toYMD(range.to) : "",
    });
  };

  const L = {
    EN: {
      checkin: "Check-in",
      checkout: "Check-out",
      legend_available: "Available",
      legend_selected: "Selected",
      legend_blocked: "Reserved",
      loading: "Loading availability...",
      clear: "Clear dates",
    },
    AL: {
      checkin: "Hyrja",
      checkout: "Dalja",
      legend_available: "E lirë",
      legend_selected: "E zgjedhur",
      legend_blocked: "E zënë",
      loading: "Duke ngarkuar...",
      clear: "Fshi datat",
    },
  }[lang] || {};

  return (
    <div className="date-range-picker" data-testid="date-range-picker">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div
          className="p-3"
          style={{
            border: "1px solid var(--color-border)",
            background: selected.from ? "#fff" : "transparent",
          }}
          data-testid="drp-checkin-display"
        >
          <div className="overline mb-1" style={{ color: "var(--color-secondary)" }}>
            {L.checkin}
          </div>
          <div
            className="font-display text-lg"
            style={{ color: selected.from ? "var(--color-primary)" : "var(--color-muted)" }}
          >
            {selected.from ? selected.from.toLocaleDateString(lang === "AL" ? "sq-AL" : "en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </div>
        </div>
        <div
          className="p-3"
          style={{
            border: "1px solid var(--color-border)",
            background: selected.to ? "#fff" : "transparent",
          }}
          data-testid="drp-checkout-display"
        >
          <div className="overline mb-1" style={{ color: "var(--color-secondary)" }}>
            {L.checkout}
          </div>
          <div
            className="font-display text-lg"
            style={{ color: selected.to ? "var(--color-primary)" : "var(--color-muted)" }}
          >
            {selected.to ? selected.to.toLocaleDateString(lang === "AL" ? "sq-AL" : "en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm" style={{ color: "var(--color-muted)" }}>
          {L.loading}
        </div>
      ) : (
        <div className="drp-wrap">
          <DayPicker
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            numberOfMonths={2}
            pagedNavigation
            weekStartsOn={1}
            locale={lang === "AL" ? { options: { weekStartsOn: 1 } } : undefined}
            data-testid="drp-calendar"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-4 mt-5 text-xs items-center" style={{ color: "var(--color-muted)" }}>
        <div className="flex items-center gap-2">
          <span className="drp-legend-box drp-legend-available"></span>
          {L.legend_available}
        </div>
        <div className="flex items-center gap-2">
          <span className="drp-legend-box drp-legend-selected"></span>
          {L.legend_selected}
        </div>
        <div className="flex items-center gap-2">
          <span className="drp-legend-box drp-legend-blocked"></span>
          {L.legend_blocked}
        </div>
        {(selected.from || selected.to) && (
          <button
            type="button"
            onClick={() => onChange({ check_in: "", check_out: "" })}
            className="ml-auto text-xs uppercase tracking-wider hover:underline"
            style={{ color: "var(--color-secondary)" }}
            data-testid="drp-clear"
          >
            {L.clear}
          </button>
        )}
      </div>
    </div>
  );
}
